import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const AgentPaymentsPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // all, has_debt, clear
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCompaniesDebt();
  }, []);

  const fetchCompaniesDebt = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/field-agent/payments/companies');
      setCompanies(response.data.companies);
    } catch (error) {
      console.error('Error fetching companies debt:', error);
      toast.error('שגיאה בטעינת נתוני חובות');
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    // Filter by status
    let statusMatch = true;
    if (filterStatus === 'has_debt') statusMatch = company.current_balance > 0;
    if (filterStatus === 'clear') statusMatch = company.current_balance <= 0;
    
    // Filter by search query
    const searchMatch = searchQuery === '' || 
      company.company_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    return statusMatch && searchMatch;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getStatusBadge = (company) => {
    if (company.status === 'no_credit') {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">ללא מסגרת</span>;
    }
    if (company.current_balance > 0) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">חוב פתוח</span>;
    }
    if (company.current_balance < 0) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">עודף תשלום</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">מאוזן</span>;
  };

  const getCreditUtilizationColor = (utilization) => {
    if (utilization >= 999) return 'text-red-700 font-bold'; // Over limit
    if (utilization >= 90) return 'text-red-600';
    if (utilization >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };
  
  const formatUtilization = (utilization) => {
    if (utilization === undefined || utilization === null || isNaN(utilization)) {
      return '0%';
    }
    if (utilization >= 999) {
      return 'חריגה';
    }
    return `${utilization}%`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 py-6" dir="rtl">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">ניהול גבייה</h1>
        <p className="text-sm text-gray-600">צפייה בחובות וגבייה מחברות</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mb-4">
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ חברות</div>
          <div className="text-xl font-bold text-gray-900">{companies.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">חברות עם חוב</div>
          <div className="text-xl font-bold text-red-600">
            {companies.filter(c => c.current_balance > 0).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ חובות</div>
          <div className="text-xl font-bold text-red-600">
            {formatCurrency(companies.reduce((sum, c) => sum + Math.max(0, c.current_balance), 0))}
          </div>
          {companies.some(c => c.current_balance < 0) && (
            <div className="text-xs text-green-600 mt-1">
              עודף: {formatCurrency(Math.abs(companies.reduce((sum, c) => sum + Math.min(0, c.current_balance), 0)))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ נגבה</div>
          <div className="text-xl font-bold text-green-600">
            {formatCurrency(companies.reduce((sum, c) => sum + (c.collected_amount || 0), 0))}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3">
          <div className="text-xs text-gray-600 mb-1">סה"כ זיכויים</div>
          <div className="text-xl font-bold text-orange-600">
            {formatCurrency(companies.reduce((sum, c) => sum + (c.refunds_total || 0), 0))}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-3 mb-4">
        {/* Search Bar */}
        <div className="mb-3">
          <div className="relative">
            <input
              type="text"
              placeholder="חיפוש לפי שם חברה..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pr-10 pl-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute right-3 top-2.5 text-gray-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {/* Status Filters */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              filterStatus === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            הכל ({companies.length})
          </button>
          <button
            onClick={() => setFilterStatus('has_debt')}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              filterStatus === 'has_debt'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            עם חוב ({companies.filter(c => c.current_balance > 0).length})
          </button>
          <button
            onClick={() => setFilterStatus('clear')}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              filterStatus === 'clear'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            מאוזנות ({companies.filter(c => c.current_balance <= 0).length})
          </button>
        </div>
        
        {/* Results count */}
        {searchQuery && (
          <div className="mt-2 text-xs text-gray-600">
            נמצאו {filteredCompanies.length} תוצאות
          </div>
        )}
      </div>

      {/* Companies Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  חברה
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  מסגרת
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  חוב פתיחה
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  הזמנות
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  נגבה
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  חוב נוכחי
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  ניצול
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  סטטוס
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <tr key={company.company_id} className="hover:bg-gray-50">
                  <td className="px-2 py-2">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]" title={company.company_name}>
                      {company.company_name}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-sm text-gray-900 whitespace-nowrap">
                    {formatCurrency(company.credit_limit)}
                  </td>
                  <td className="px-2 py-2 text-sm text-blue-600 whitespace-nowrap">
                    {company.opening_balance > 0 ? formatCurrency(company.opening_balance) : '-'}
                  </td>
                  <td className="px-2 py-2 text-sm text-gray-500 whitespace-nowrap">
                    {formatCurrency(company.orders_total || company.used_credit || 0)}
                  </td>
                  <td className="px-2 py-2 text-sm text-green-600 font-medium whitespace-nowrap">
                    {formatCurrency(company.collected_amount)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className={`text-sm font-bold ${company.current_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(company.current_balance)}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getCreditUtilizationColor(company.utilization)}`}>
                      {formatUtilization(company.utilization)}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {getStatusBadge(company)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    <button
                      onClick={() => navigate(`/agent/payments/${company.company_id}`)}
                      className="text-primary-600 hover:text-primary-800 font-medium"
                    >
                      פרטים
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCompanies.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">לא נמצאו תוצאות</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery 
                ? `לא נמצאו חברות עם השם "${searchQuery}"`
                : filterStatus === 'all' ? 'לא נמצאו חברות' : 'לא נמצאו חברות בסינון זה'}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                נקה חיפוש
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentPaymentsPage;

